import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb.js';
import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb.js';
import {
  ReportRequest_InputType_CBCT_IOS_Superimposition,
  ReportRequest_InputType_StudioImplant,
  ReportRequest_InputType_StudioOrtho,
} from '@/shared/api/protocol-ts/model/dto_report_request_pb.js';
import { Report_CBCT_IOS_Superimposition_Orientation } from '@/shared/api/protocol-ts/model/dto_report_type_cbct_ios_superimposition_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { OrderReportActionParams, OrderReportType } from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import {
  getRawMeshViewerLink,
  openInNewBrowserTab,
  trackEvent,
} from '@/shared/lib';
import { Checkbox, Icon, Modal, RadioGroup, Tooltip } from '@/shared/ui';

import {
  billingModel,
  DiagnocoinNotice,
  isCoinPayableOrderReportType,
  ORDER_REPORT_TYPE_SERVICE_TYPES,
} from '@/entities/billing';
import { composedModel } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization/index.ts';
import { patientModel } from '@/entities/patient';
import { superimpositionOrientationLabels } from '@/entities/reports';
import { studyModel } from '@/entities/study';

import { useNewReport } from '@/features/addNewReport';
import { orderReportSelectors } from '@/features/orderReport';
import { useUploadAssetContext } from '@/features/uploadAsset';

import {
  ORDER_REPORT_ASSET_TYPE,
  ORDER_REPORT_STUDY_TYPE,
  OrderReportModalStep,
  SUPERIMPOSITION_ORIENTATION_OPTIONS,
  UPLOAD_STUDY_FORM_ID,
  UploadStudyPayload,
  uploadStudySchema,
} from '../../config';
import {
  orderReportTitle,
  superimpositionOrientationInfo,
} from '../../config/i18n';
import {
  selectFirstStudyID,
  selectSecondaryCBCTStudyID,
} from '../../model/orderReportModal.selectors';
import { DentalPhotosStep } from '../DentalPhotosStep/DentalPhotosStep.tsx';
import { OrderReportStep } from '../OrderReportStep/OrderReportStep.tsx';

import styles from './OrderReportModal.module.scss';

export const OrderReportModal = () => {
  const dispatch = useAppDispatch();
  const { patientID } = useParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [IOSMeshesFiles, setIOSMeshesFFiles] = useState<FileWithPath[]>([]);
  const [secondaryCBCTFiles, setSecondaryCBCTFiles] = useState<FileWithPath[]>(
    [],
  );
  const [filesUploadErrorMessage, setFilesUploadErrorMessage] = useState('');
  const [
    IOSMeshesFilesUploadErrorMessage,
    setIOSMeshesFilesUploadErrorMessage,
  ] = useState('');
  const [
    secondaryCBCTFilesUploadErrorMessage,
    setSecondaryCBCTFilesUploadErrorMessage,
  ] = useState('');
  const [showSecondaryCBCTSelect, setShowSecondaryCBCTSelect] = useState(false);
  const [shouldPlanSurgicalGuide, setShouldPlanSurgicalGuide] = useState(true);
  const [selectedDentalPhotoIDs, setSelectedDentalPhotoIDs] = useState<
    string[]
  >([]);

  const {
    visible,
    data: { reportType },
  } = useAppSelector(modalModel.selectors.selectOrderReportModal);

  const enableOrthoStudio = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_OrthoStudio,
    ),
  );
  const isImplantStudioAnalysisAvailable = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Order_ImplantStudio_Analysis,
    ),
  );
  const isCBCTIOSSuperimpositionAnalysisAvailable = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Order_CBCT_IOS_Superimposition_Analysis,
    ),
  );
  const isOrthoStudioAnalysisAvailable =
    useAppSelector(
      billingModel.selectors.selectIsServiceActiveByType(
        ServiceType.ServiceType_Order_OrthoStudio_Analysis,
      ),
    ) || enableOrthoStudio;
  const showOrthoStudioAddSecondaryCBCT = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_OrthoStudioAddSecondaryCBCT,
    ),
  );
  const isUploadPhotoServiceActive = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Enable_UploadDentalPhoto,
    ),
  );
  const isIOSBlockedByUploadRestriction = useAppSelector(
    orderReportSelectors.selectIsIOSBlockedByUploadRestriction,
  );
  const isCBCTAlignmentBlockedByUploadRestriction = useAppSelector(
    orderReportSelectors.selectIsCBCTAlignmentBlockedByUploadRestriction,
  );

  const patientName = useAppSelector(
    patientModel.selectors.selectPatientFullName(patientID as string),
  );
  const firstMainStudyID = useAppSelector(
    selectFirstStudyID(ORDER_REPORT_STUDY_TYPE[reportType]),
  );
  const firstIOSMeshesStudyID = useAppSelector(
    selectFirstStudyID(StudyType.StudyType_IOS_Meshes),
  );
  const dentalPhotoStudies = useAppSelector(
    studyModel.selectors.selectDentalPhotoStudies,
  );

  const { startUploadAsset: startUploadStudy } = useUploadAssetContext();

  const {
    requestCBCTGPReport,
    requestIOXRayGPReport,
    requestPanoGPReport,
    requestCBCTSegmentationReport,
    requestStudioOrthoReport,
    requestStudioImplantReport,
    requestCBCTIOSSuperimpositionReport,
    requestCBCTOrthoReport,
    requestIOSSegmentationReport,
  } = useNewReport();

  const { watch, control, handleSubmit, reset, resetField, setValue } =
    useForm<UploadStudyPayload>({
      resolver: yupResolver(uploadStudySchema),
    });

  const canOrderImplantStudioReport =
    isImplantStudioAnalysisAvailable && reportType === 'ImplantStudio';
  const canOrderCBCTIOSSuperimpositionReport =
    isCBCTIOSSuperimpositionAnalysisAvailable && reportType === '3DStudio';
  const canOrderOrthoStudioAnalysis =
    isOrthoStudioAnalysisAvailable && reportType === 'OrthoStudio';
  const isOnlyUploadReportType =
    reportType === 'IOSSegmentation' || reportType === 'RawMeshViewer';

  const firstStepStudyAssetType = ORDER_REPORT_ASSET_TYPE[reportType];

  const isSecondStepAvailable =
    !isIOSBlockedByUploadRestriction &&
    ((canOrderImplantStudioReport && shouldPlanSurgicalGuide) ||
      canOrderCBCTIOSSuperimpositionReport ||
      canOrderOrthoStudioAnalysis);

  const isSecondStepOptional =
    canOrderCBCTIOSSuperimpositionReport || canOrderOrthoStudioAnalysis;

  const currentMainStudyID = watch('mainStudyID');

  const secondaryCBCTStudyID = useAppSelector(
    selectSecondaryCBCTStudyID(currentMainStudyID ?? ''),
  );

  const currentIOSMeshesStudyID = watch('IOSMeshesStudyID');
  const currentSecondaryCBCTStudyID = watch('SecondaryCBCTStudyID');

  const uploadFilesWithError = !!(
    filesUploadErrorMessage ||
    IOSMeshesFilesUploadErrorMessage ||
    secondaryCBCTFilesUploadErrorMessage
  );

  const isMainFilesExists = files.length > 0;
  const isIOSMeshesFilesExists = IOSMeshesFiles.length > 0;
  const isSecondaryCBCTFilesExists = secondaryCBCTFiles.length > 0;

  const isMainStudySelectedOrFilesToUpload =
    currentMainStudyID || isMainFilesExists;
  const isMeshedStudySelectedOrFilesToUpload =
    currentIOSMeshesStudyID || isIOSMeshesFilesExists;
  const isSecondaryCBCTSelectedOrFilesToUpload =
    currentSecondaryCBCTStudyID || isSecondaryCBCTFilesExists;

  const showDentalPhotosSelect =
    (canOrderImplantStudioReport ||
      canOrderCBCTIOSSuperimpositionReport ||
      canOrderOrthoStudioAnalysis) &&
    isMeshedStudySelectedOrFilesToUpload &&
    isUploadPhotoServiceActive &&
    dentalPhotoStudies.length > 0;

  const orderedServiceTypes = useMemo<readonly ServiceType[]>(() => {
    if (reportType === '3DStudio') {
      return canOrderCBCTIOSSuperimpositionReport &&
        isMeshedStudySelectedOrFilesToUpload
        ? [ServiceType.ServiceType_Order_CBCT_IOS_Superimposition_Analysis]
        : [ServiceType.ServiceType_Order_CBCT_STL_Analysis];
    }

    if (reportType === 'OrthoStudio') {
      return canOrderOrthoStudioAnalysis
        ? [ServiceType.ServiceType_Order_OrthoStudio_Analysis]
        : [ServiceType.ServiceType_Order_CBCT_Orthodontic_Analysis];
    }

    return isCoinPayableOrderReportType(reportType)
      ? ORDER_REPORT_TYPE_SERVICE_TYPES[reportType]
      : [];
  }, [
    reportType,
    canOrderCBCTIOSSuperimpositionReport,
    canOrderOrthoStudioAnalysis,
    isMeshedStudySelectedOrFilesToUpload,
  ]);

  const selectOrderFunding = useMemo(
    () =>
      composedModel.selectors.makeSelectDiagnocoinFunding(orderedServiceTypes),
    [orderedServiceTypes],
  );
  const funding = useAppSelector(selectOrderFunding);
  const isCoinFunded = funding.source === 'diagnocoins';

  const isSubmitEnabled =
    isMainStudySelectedOrFilesToUpload &&
    (!isSecondStepAvailable ||
      isSecondStepOptional ||
      isMeshedStudySelectedOrFilesToUpload) &&
    (!showSecondaryCBCTSelect || isSecondaryCBCTSelectedOrFilesToUpload) &&
    !uploadFilesWithError;

  const orderReportActions: Record<
    OrderReportType,
    (params: OrderReportActionParams) => void
  > = {
    CBCT: ({ mainStudyID }) => {
      requestCBCTGPReport(mainStudyID);

      trackEvent({
        eventName: 'PatientProfilePageCBCTOrderReportButtonClicked',
      });
    },
    Pano: ({ mainStudyID }) => {
      requestPanoGPReport(mainStudyID);

      trackEvent({
        eventName: 'PatientProfilePagePanoOrderReportButtonClicked',
      });
    },
    IOXRay: ({ mainStudyID }) => {
      requestIOXRayGPReport(mainStudyID);

      trackEvent({
        eventName: 'PatientProfilePageIOXRayAIOrderReportButtonClicked',
      });
    },
    IOSSegmentation: ({ mainStudyID }) => {
      requestIOSSegmentationReport(mainStudyID);

      trackEvent({
        eventName: 'PatientProfilePage3DModelOrderReportButtonClicked',
      });
    },
    RawMeshViewer: ({ mainStudyID }) => {
      openInNewBrowserTab(getRawMeshViewerLink(mainStudyID));
    },
    '3DStudio': ({
      mainStudyID,
      IOSMeshesStudyID,
      superimpositionOrientation,
      dentalPhotoStudyIDs,
    }) => {
      if (isCBCTIOSSuperimpositionAnalysisAvailable && IOSMeshesStudyID) {
        requestCBCTIOSSuperimpositionReport(
          new ReportRequest_InputType_CBCT_IOS_Superimposition({
            CBCTStudyID: mainStudyID,
            STLStudyID: IOSMeshesStudyID,
            DentalPhotoStudyIDs: dentalPhotoStudyIDs,
            Orientation: superimpositionOrientation,
          }),
        );
      } else {
        requestCBCTSegmentationReport(mainStudyID);
      }

      trackEvent({
        eventName: 'PatientProfilePage3DModelOrderReportButtonClicked',
      });
    },
    ImplantStudio: ({ mainStudyID, IOSMeshesStudyID, dentalPhotoStudyIDs }) => {
      requestStudioImplantReport(
        new ReportRequest_InputType_StudioImplant({
          CBCTStudyID: mainStudyID,
          IOSMeshesStudyID,
          DentalPhotoStudyIDs: dentalPhotoStudyIDs,
        }),
      );

      trackEvent({
        eventName: 'PatientProfilePageImplantStudioOrderReportButtonClicked',
      });
    },
    OrthoStudio: ({
      mainStudyID,
      IOSMeshesStudyID,
      SecondaryCBCTStudyID,
      dentalPhotoStudyIDs,
    }) => {
      if (isOrthoStudioAnalysisAvailable) {
        requestStudioOrthoReport(
          new ReportRequest_InputType_StudioOrtho({
            CBCTStudyID: mainStudyID,
            IOSMeshesStudyID,
            DentalPhotoStudyIDs: dentalPhotoStudyIDs,
            SecondaryCBCTStudyID,
          }),
        );
      } else {
        requestCBCTOrthoReport(mainStudyID);
      }

      if (showSecondaryCBCTSelect) {
        showSecondaryCBCTSelectToggle();
      }

      trackEvent({
        eventName: 'PatientProfilePageOrthoStudioOrderReportButtonClicked',
      });
    },
    Transcriptor: () => undefined,
  };

  const setErrorMessage =
    (filesStep: OrderReportModalStep) => (errorMessage: string) => {
      switch (filesStep) {
        case 1: {
          setFilesUploadErrorMessage(errorMessage);
          break;
        }
        case 2: {
          setIOSMeshesFilesUploadErrorMessage(errorMessage);
          break;
        }
        case 3: {
          setSecondaryCBCTFilesUploadErrorMessage(errorMessage);
        }
      }
    };

  const addFiles =
    (filesStep: OrderReportModalStep) => (acceptedFiles: FileWithPath[]) => {
      switch (filesStep) {
        case 1: {
          setFilesUploadErrorMessage('');
          setFiles((currentFiles) => currentFiles.concat(acceptedFiles));
          break;
        }
        case 2: {
          setIOSMeshesFilesUploadErrorMessage('');
          setIOSMeshesFFiles((currentFiles) =>
            currentFiles.concat(acceptedFiles),
          );
          break;
        }
        case 3: {
          setSecondaryCBCTFilesUploadErrorMessage('');
          setSecondaryCBCTFiles((currentFiles) =>
            currentFiles.concat(acceptedFiles),
          );
        }
      }
    };

  const removeFiles = (filesStep: OrderReportModalStep) => () => {
    switch (filesStep) {
      case 1: {
        setFiles([]);
        resetField('mainStudyID');
        break;
      }
      case 2: {
        setIOSMeshesFFiles([]);
        resetField('IOSMeshesStudyID');
        break;
      }
      case 3: {
        setSecondaryCBCTFiles([]);
        resetField('SecondaryCBCTStudyID');
      }
    }
  };

  const removeAllFiles = () => {
    setFiles([]);
    setIOSMeshesFFiles([]);
    setSecondaryCBCTFiles([]);
  };

  const shouldPlanSurgicalGuideToggle = () => {
    setShouldPlanSurgicalGuide(!shouldPlanSurgicalGuide);

    if (shouldPlanSurgicalGuide) {
      resetField('IOSMeshesStudyID');
      setSelectedDentalPhotoIDs([]);
      setIOSMeshesFFiles([]);
      setIOSMeshesFilesUploadErrorMessage('');
    } else {
      setValue('IOSMeshesStudyID', firstIOSMeshesStudyID);
    }
  };

  const showSecondaryCBCTSelectToggle = () => {
    setShowSecondaryCBCTSelect(!showSecondaryCBCTSelect);

    if (showSecondaryCBCTSelect) {
      resetField('SecondaryCBCTStudyID');
    } else {
      setValue('SecondaryCBCTStudyID', secondaryCBCTStudyID);
    }
  };

  const handleClose = useCallback(() => {
    reset();

    setFilesUploadErrorMessage('');
    setIOSMeshesFilesUploadErrorMessage('');
    setSecondaryCBCTFilesUploadErrorMessage('');

    dispatch(modalModel.actions.closeModal(ModalID.OrderReport));

    removeAllFiles();

    if (showSecondaryCBCTSelect) {
      showSecondaryCBCTSelectToggle();
    }
  }, [removeAllFiles]);

  const onSubmit: SubmitHandler<UploadStudyPayload> = async (data) => {
    handleClose();
    dispatch(modalModel.actions.closeModal(ModalID.MobileOrderReportModal));

    const uploadStudyPromises: Promise<void>[] = [];
    const {
      mainStudyID: currentMainStudyID,
      IOSMeshesStudyID: currentIOSMeshesStudyID,
      SecondaryCBCTStudyID: currentSecondaryCBCTStudyID,
    } = data;

    let mainStudyID = currentMainStudyID;
    let IOSMeshesStudyID = currentIOSMeshesStudyID;
    let DentalPhotoStudyIDs: string[] = [];
    let SecondaryCBCTStudyID = currentSecondaryCBCTStudyID;

    if (isMainFilesExists) {
      const uploadStudyPromise = startUploadStudy({
        patientID,
        files,
        assetType: firstStepStudyAssetType,
        rawMeshViewer: reportType === 'RawMeshViewer',
        meta: {
          studyType: firstStepStudyAssetType,
          patientName,
        },
      }).then(({ studyID }) => {
        mainStudyID = studyID;
      });

      uploadStudyPromises.push(uploadStudyPromise);
    }

    if (isIOSMeshesFilesExists) {
      const uploadIOSMeshesStudyPromise = startUploadStudy({
        patientID,
        files: IOSMeshesFiles,
        assetType: AssetType.AssetType_Study_IOS_Meshes,
        meta: {
          studyType: AssetType.AssetType_Study_IOS_Meshes,
          patientName,
        },
      }).then(({ studyID }) => {
        IOSMeshesStudyID = studyID;
      });

      uploadStudyPromises.push(uploadIOSMeshesStudyPromise);
    }

    if (isSecondaryCBCTFilesExists) {
      const uploadSecondaryCBCTStudyPromise = startUploadStudy({
        patientID,
        files: secondaryCBCTFiles,
        assetType: AssetType.AssetType_Study_CBCT,
        meta: {
          studyType: AssetType.AssetType_Study_CBCT,
          patientName,
        },
      }).then(({ studyID }) => {
        SecondaryCBCTStudyID = studyID;
      });

      uploadStudyPromises.push(uploadSecondaryCBCTStudyPromise);
    }

    const uploadStudyResults = await Promise.allSettled(uploadStudyPromises);
    // TODO: remove logic from DentalPhotoStudyIDs to StudyAssetIDs after protocol update
    if (isUploadPhotoServiceActive && dentalPhotoStudies.length > 0) {
      // @ts-expect-error WARN: after protocol refactoring type will change
      DentalPhotoStudyIDs = selectedDentalPhotoIDs.map(
        (selectedAssetID) =>
          dentalPhotoStudies.find(
            (dentalPhotoStudy) =>
              selectedAssetID === dentalPhotoStudy.AssetIDs.at(0),
          )?.ID,
      );
    }

    if (
      mainStudyID &&
      !uploadStudyResults.some((result) => result.status === 'rejected')
    ) {
      const dentalPhotoStudyIDs = (DentalPhotoStudyIDs ?? []).filter(
        (studyID): studyID is string => Boolean(studyID),
      );
      const shouldSkipAutoOpenRawMeshViewer =
        reportType === 'RawMeshViewer' && isMainFilesExists;

      if (!shouldSkipAutoOpenRawMeshViewer) {
        orderReportActions[reportType]({
          mainStudyID,
          IOSMeshesStudyID,
          SecondaryCBCTStudyID,
          superimpositionOrientation: data.superimpositionOrientation,
          dentalPhotoStudyIDs,
        });
      }

      setSelectedDentalPhotoIDs([]);
    }
  };

  useEffect(() => {
    reset({
      mainStudyID: isOnlyUploadReportType ? undefined : firstMainStudyID,
      IOSMeshesStudyID:
        isSecondStepOptional || !shouldPlanSurgicalGuide
          ? undefined
          : firstIOSMeshesStudyID,
      SecondaryCBCTStudyID: undefined,
      superimpositionOrientation:
        Report_CBCT_IOS_Superimposition_Orientation.CBCT_IOS_Superimposition_Orientation_CBCT,
    });
  }, [
    isOnlyUploadReportType,
    firstMainStudyID,
    firstIOSMeshesStudyID,
    isSecondStepOptional,
    shouldPlanSurgicalGuide,
  ]);

  useEffect(() => {
    if (
      showSecondaryCBCTSelect &&
      currentMainStudyID === currentSecondaryCBCTStudyID
    ) {
      setValue('SecondaryCBCTStudyID', secondaryCBCTStudyID);
    }
  }, [currentMainStudyID]);

  return (
    <Modal
      data-component="order-report-modal"
      containerClassName={styles.modalContainer}
      bodyClassName={styles.modalBody}
      title={formatMessage(orderReportTitle[reportType])}
      isOpen={visible}
      onCancel={handleClose}
      borderless
      okButtonText={
        reportType === 'RawMeshViewer' ? (
          <FormattedMessage
            id="orderReportModal.uploadButtonText"
            defaultMessage="Upload"
          />
        ) : isCoinFunded ? (
          <span className={styles.orderForCoins}>
            <FormattedMessage
              id="orderReportModal.orderForCoinsButtonText"
              defaultMessage="Order for {coins}"
              values={{ coins: funding.coins }}
            />

            <span className={styles.orderForCoinsGlyph}>
              <Icon name="cat" size={13} />
            </span>
          </span>
        ) : (
          <FormattedMessage
            id="orderReportModal.applyButtonText"
            defaultMessage="Order"
          />
        )
      }
      okButtonProps={{
        type: 'submit',
        form: UPLOAD_STUDY_FORM_ID,
        disabled: !isSubmitEnabled,
      }}
      cancelButtonProps={{
        variant: 'tertiary',
      }}
      shouldRenderCancelButton={!isMobile}
    >
      <form
        id={UPLOAD_STUDY_FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className={styles.container}
      >
        <OrderReportStep
          control={control}
          currentStudyID={currentMainStudyID}
          step={isSecondStepAvailable ? 1 : undefined}
          orderReportType={reportType}
          studyType={ORDER_REPORT_STUDY_TYPE[reportType]}
          assetType={firstStepStudyAssetType}
          errorMessage={filesUploadErrorMessage}
          files={files}
          onlyUpload={isOnlyUploadReportType}
          hideTitle={reportType === 'RawMeshViewer'}
          onAddFiles={addFiles(1)}
          onDeleteFiles={removeFiles(1)}
          onError={setErrorMessage(1)}
        />

        {canOrderImplantStudioReport && !isIOSBlockedByUploadRestriction && (
          <Checkbox
            checked={shouldPlanSurgicalGuide}
            onChange={shouldPlanSurgicalGuideToggle}
            className={styles.checkbox}
          >
            <div className={styles.checkboxContent}>
              <FormattedMessage
                id="orderReportModal.planSurgicalGuide"
                defaultMessage="Plan surgical guide"
              />

              <Tooltip.Primary
                content={formatMessage({
                  id: 'orderReportModal.planSurgicalGuideInfo',
                  defaultMessage:
                    'Surgical guide planning requires an intraoral scan',
                })}
                triggerClassName={styles.infoTooltip}
              >
                <Icon name="info" size={28} className={styles.infoIcon} />
              </Tooltip.Primary>
            </div>
          </Checkbox>
        )}

        {isSecondStepAvailable && (
          <OrderReportStep
            control={control}
            currentStudyID={currentIOSMeshesStudyID}
            step={2}
            orderReportType={reportType}
            studyType={StudyType.StudyType_IOS_Meshes}
            assetType={AssetType.AssetType_Study_IOS_Meshes}
            errorMessage={IOSMeshesFilesUploadErrorMessage}
            files={IOSMeshesFiles}
            onAddFiles={addFiles(2)}
            onDeleteFiles={removeFiles(2)}
            onError={setErrorMessage(2)}
            onReset={() => resetField('IOSMeshesStudyID')}
            isOptional={isSecondStepOptional}
            infoTooltip={{
              show: isSecondStepAvailable,
              message: formatMessage({
                id: 'orderReportModal.scanUploadInfo',
                defaultMessage:
                  'Requires at least one scan — upper jaw, lower jaw, or both. Supported formats: STL, OBJ, PLY, DRC',
              }),
            }}
          />
        )}

        {canOrderCBCTIOSSuperimpositionReport &&
          isMeshedStudySelectedOrFilesToUpload && (
            <div className={styles.orientationSection}>
              <div className={styles.orientationLabel}>
                <FormattedMessage
                  id="orderReportModal.orientation.title"
                  defaultMessage="Superimposition orientation"
                />

                <Tooltip.Primary
                  content={
                    <ul className={styles.orientationInfoList}>
                      {SUPERIMPOSITION_ORIENTATION_OPTIONS.map(
                        (orientation) => (
                          <li key={orientation}>
                            {formatMessage(
                              superimpositionOrientationInfo[orientation],
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  }
                  triggerClassName={styles.infoTooltip}
                >
                  <Icon name="info" size={28} className={styles.infoIcon} />
                </Tooltip.Primary>
              </div>

              <Controller
                control={control}
                name="superimpositionOrientation"
                render={({ field: { ref, value, name, onChange, onBlur } }) => (
                  <RadioGroup
                    ref={ref}
                    name={name}
                    variant="tab"
                    value={value}
                    onBlur={onBlur}
                    onChange={(event) => onChange(Number(event.target.value))}
                    items={SUPERIMPOSITION_ORIENTATION_OPTIONS.map(
                      (orientation) => ({
                        value: orientation,
                        label: formatMessage(
                          superimpositionOrientationLabels[orientation],
                        ),
                      }),
                    )}
                  />
                )}
              />
            </div>
          )}

        {showDentalPhotosSelect && (
          <DentalPhotosStep
            selectedIDs={selectedDentalPhotoIDs}
            setSelectedIDs={setSelectedDentalPhotoIDs}
          />
        )}

        {canOrderOrthoStudioAnalysis &&
          showOrthoStudioAddSecondaryCBCT &&
          !isCBCTAlignmentBlockedByUploadRestriction && (
            <>
              <Checkbox
                checked={showSecondaryCBCTSelect}
                onChange={showSecondaryCBCTSelectToggle}
                className={styles.checkbox}
              >
                <div className={styles.checkboxContent}>
                  <FormattedMessage
                    id="orderReportModal.addPreviousCBCT"
                    defaultMessage="Add previous CBCT"
                  />

                  <Tooltip.Primary
                    content={formatMessage({
                      id: 'orderReportModal.addPreviousCBCTInfo',
                      defaultMessage:
                        'Secondary CBCT will be superimposed with the main CBCT',
                    })}
                    triggerClassName={styles.infoTooltip}
                  >
                    <Icon name="info" size={28} className={styles.infoIcon} />
                  </Tooltip.Primary>
                </div>
              </Checkbox>

              {showSecondaryCBCTSelect && (
                <OrderReportStep
                  control={control}
                  currentStudyID={currentSecondaryCBCTStudyID}
                  step={3}
                  orderReportType={'CBCT'}
                  studyType={StudyType.StudyType_CBCT}
                  assetType={AssetType.AssetType_Study_CBCT}
                  errorMessage={secondaryCBCTFilesUploadErrorMessage}
                  files={secondaryCBCTFiles}
                  isAlignmentCBCTReportOrder={showSecondaryCBCTSelect}
                  onAddFiles={addFiles(3)}
                  onDeleteFiles={removeFiles(3)}
                  onError={setErrorMessage(3)}
                />
              )}
            </>
          )}

        <DiagnocoinNotice funding={funding} />
      </form>
    </Modal>
  );
};
